import { driver, DriveStep } from 'driver.js';
import 'driver.js/dist/driver.css';
import { baseTourConfig, injectTourStyles } from './tourConfig';
import { TourId } from '@/store/tourStore';

export const TOUR_ID: TourId = 'explore-competitions';

/**
 * Walkthrough of the Competitions tree's hidden affordances (the page's core
 * interactions are hover/double-click and were invisible to new admins):
 * expand chevron → rename → ⋯ menu → Active switch → Add buttons.
 *
 * Steps are built DYNAMICALLY: element steps whose target isn't in the DOM
 * (empty org, section without rows) are dropped so driver.js never highlights
 * a missing element — the section-level fallbacks still tell the story.
 */
export const createSteps = (): DriveStep[] => {
  const present = (selector: string) => !!document.querySelector(selector);

  const steps: (DriveStep | null)[] = [
    {
      popover: {
        title: 'Welcome to Competitions',
        description: 'This is your whole structure in one tree: tournaments, leagues, their divisions, and teams. Let\'s uncover everything each row can do.',
        side: 'over',
        align: 'center',
      },
    },
    present('[data-tour="organize-row-expand"]') ? {
      element: '[data-tour="organize-row-expand"]',
      popover: {
        title: 'Expand to see inside',
        description: 'Every tournament and league has more underneath. Click the arrow to reveal its contests and divisions, and expand a division to see its teams.',
        side: 'bottom',
        align: 'start',
      },
    } : null,
    present('[data-tour="organize-row-name"]') ? {
      element: '[data-tour="organize-row-name"]',
      popover: {
        title: 'Rename anything in place',
        description: 'Double-click any name to rename it right here, or use the pencil that appears when you hover a row. Enter saves, Escape cancels.',
        side: 'bottom',
        align: 'start',
      },
    } : null,
    present('[data-tour="organize-row-menu"]') ? {
      element: '[data-tour="organize-row-menu"]',
      popover: {
        title: 'The ⋯ menu',
        description: 'Hover a row to reveal its actions. The ⋯ menu holds Settings, Payment, Admins, Tiebreakers, Clone, and Delete for that row.',
        side: 'bottom',
        align: 'end',
      },
    } : null,
    present('[data-tour="organize-row-active"]') ? {
      element: '[data-tour="organize-row-active"]',
      popover: {
        title: 'Active = visible',
        description: 'The switch controls whether this competition shows on your public site. Off means archived. Nothing is deleted.',
        side: 'bottom',
        align: 'end',
      },
    } : null,
    present('[data-tour="organize-add-tournament"]') ? {
      element: '[data-tour="organize-add-tournament"]',
      popover: {
        title: 'Add more anytime',
        description: 'Create a new tournament or league here. The dialog builds its divisions and opens registration in the same step.',
        side: 'bottom',
        align: 'start',
      },
    } : (present('[data-tour="organize-add-league"]') ? {
      element: '[data-tour="organize-add-league"]',
      popover: {
        title: 'Add more anytime',
        description: 'Create a new league here. The dialog builds its divisions and opens registration in the same step.',
        side: 'bottom',
        align: 'start',
      },
    } : null),
    {
      popover: {
        title: 'That\'s the tree!',
        description: 'Drag rows to reorder, drag teams between divisions, and use the hint bar up top if you forget. You can replay this tour from "Take a tour".',
        side: 'over',
        align: 'center',
      },
    },
  ];

  return steps.filter((s): s is DriveStep => s !== null);
};

export const startOrganizeTour = (onComplete?: () => void, _navigate?: (path: string) => void) => {
  injectTourStyles();

  const driverObj = driver({
    ...baseTourConfig,
    steps: createSteps(),
    onDestroyStarted: () => {
      onComplete?.();
      driverObj.destroy();
    },
  });

  driverObj.drive();
  return driverObj;
};
